import { isEmployeeAndOptedOut, optOutEmployee } from './employee_opt_out';
import { getQueryParam } from '../utils/query_string';
import { isDNTEnabled } from './dnt';
import {
  pageRouteObject,
  checkoutProgressDataObject,
  addToCartItemDataObject,
  itemConversionDataObject,
  trackViewItemDataObject,
  removeFromCartItemDataObject,
  addToFavoriteDataObject,
  beginCheckoutDataObject,
  beginCheckoutConversionDataObject,
  trackPurchaseDataObject,
  trackPurchaseConversionDataObject
} from './common';

// webview mode
const minimal = new URLSearchParams(window.location.search).get('minimal');

export function initializeGoogleTrackers() {
  if (minimal === 'true') return;
  if (process.env.REACT_APP_GOOGLE_TRACKING_ENABLED !== 'true' || isDNTEnabled()) {
    console.log('Google trackers blocked.');
    return;
  }
  if (isEmployeeAndOptedOut()) {
    console.log('Employee opted out.');
    return;
  } else if (getQueryParam(window.location.search, 'employee_opt_out') === '324987') {
    optOutEmployee();
    console.log('Employee opted out.');
    return;
  }
  // Inject GTAG script.
  const element = document.getElementsByTagName('script')[0];
  if (document.getElementById('gtag-js')) return;
  const js = document.createElement('script');
  js.id = 'gtag-js';
  js.async = 1;
  js.src = `https://www.googletagmanager.com/gtag/js?id=${process.env.REACT_APP_GOOGLE_UA_ID}`;
  element.parentNode.insertBefore(js, element);
  // Initialize GTAG, and setup the trackers.
  console.log('Initializing GTag.');
  window.dataLayer = window.dataLayer || [];
  gtag('js', new Date());
  // None of the Google properties should record page views on their own, we will do this manually.
  // We also anonymize IP addresses in order to be closer to GDPR compliance.
  gtag('config', process.env.REACT_APP_GOOGLE_UA_ID, {
    send_page_view: false,
    anonymize_ip: true
  });
  gtag('config', process.env.REACT_APP_GOOGLE_AW_ID, {
    send_page_view: false,
    anonymize_ip: true
  });
}

// This function is expected to take a non-zero number of arguments.
// It is essentially the "gtag" function found in Google's "install code".
function gtag() {
  if (minimal === 'true') return;
  if (process.env.REACT_APP_GOOGLE_TRACKING_ENABLED !== 'true') return;
  if (isDNTEnabled()) return;
  if (isEmployeeAndOptedOut()) return;
  window.dataLayer.push(arguments);
}

export function googleSetUserId(userId) {
  gtag('set', { user_id: userId });
}

// For a guide on the parameters, please refer to:
// https://developers.google.com/gtagjs/reference/parameter

export function googleTrackPageView(fullRoute) {
  gtag('set', pageRouteObject(fullRoute));
  gtag('event', 'page_view');
}

export function googleTrackAddItemToCart(item, price) {
  gtag('event', 'add_to_cart', addToCartItemDataObject(item, price));
  gtag('event', 'conversion', itemConversionDataObject(price));
}

export function googleTrackRemoveItemFromCart(item, price) {
  gtag('event', 'remove_from_cart', removeFromCartItemDataObject(item, price));
}

// The price passed to this function may be null, for one reason or another.
// This could happen in case:
//  1) The user's i18nSettings have not loaded yet, so the view currency is unknown.
//  2) The item is priced on request, so no price is available meaningfully.
export function googleTrackAddToFavorites(item, price) {
  gtag('event', 'add_to_wishlist', addToFavoriteDataObject(item, price));
}

// This is called when a user starts the checkout process.
export function googleTrackBeginCheckout(cart) {
  gtag('event', 'begin_checkout', beginCheckoutDataObject(cart));
  gtag('event', 'conversion', beginCheckoutConversionDataObject(cart));
}

// This is called when a user _COMPLETES_ a checkout step.
// Steps should be numbered starting from 1.
// As of August 2, 2020, this means that:
//  - Step 1 is shipping info
//  - Step 2 is payment info
//  - Step 3 is review
export function googleTrackCheckoutProgress(cart, stepNumber, optionName) {
  gtag('event', 'checkout_progress', checkoutProgressDataObject(cart, stepNumber, optionName));
}

// This is called when a user logs in to the website.
// This refers to the explicit action of logging in, rather than the action of coming back while already having logged
// in during a previous session.
// loginMethod must be one of:
//  - "google": Logging in with Google
//  - "facebook": Logging in with Facebook
//  - "lemonade": Logging in with a PIPP account.
export function googleTrackLogin(loginMethod) {
  gtag('event', 'login', { method: loginMethod });
}

// This is called when a user signs up to the site, including when they login via a third-party service provider
// for the first time. That is, if you login through facebook for the first time, this must be called.
// signUpMethod must be one of:
//  - "google": Logging in with Google
//  - "facebook": Logging in with Facebook
//  - "lemonade": Logging in with a PIPP account.
export function googleTrackSignUp(signUpMethod) {
  gtag('event', 'sign_up', { method: signUpMethod });
}

export function googleTrackPurchase(cart, transactionId, i18nSettings) {
  gtag('event', 'purchase', trackPurchaseDataObject(cart, transactionId, i18nSettings));
  gtag('event', 'conversion', trackPurchaseConversionDataObject(cart, transactionId));
}

export function googleTrackViewItem(item) {
  gtag('event', 'view_item', trackViewItemDataObject(item));
}
